import { useCallback, useEffect, useMemo, useState } from "react";
import {
  checkCertificateUserExists,
  createAdminManualCertificate,
  fetchNextCountNumber,
  AdminProductOption,
} from "../../../services/platformApi";
import { getProductDefinition, HB_FIELD_CODE_OPTIONS, isProductKey } from "../../../config/products";
import { generateCertificatePdf } from "../../../utils/certificatePdf";
import { getCurrentDateLt } from "../../../utils/date";
import { downloadPdfBlob, sanitizeName } from "../utils";

interface UseManualCertificateManagementParams {
  availableProducts: AdminProductOption[];
}

export const useManualCertificateManagement = ({
  availableProducts,
}: UseManualCertificateManagementParams) => {
  const [manualSelectedProduct, setManualSelectedProduct] = useState("");
  const [manualName, setManualName] = useState("");
  const [manualSurname, setManualSurname] = useState("");
  const [manualBirthDate, setManualBirthDate] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [manualOptionalEmail, setManualOptionalEmail] = useState("");
  const [manualCompany, setManualCompany] = useState("");
  const [manualFieldCode, setManualFieldCode] = useState("");
  const [manualProductCode, setManualProductCode] = useState("");
  const [manualHours, setManualHours] = useState("");
  const [manualDate, setManualDate] = useState(getCurrentDateLt());
  const [manualCountNumber, setManualCountNumber] = useState("");
  const [manualPdfBlob, setManualPdfBlob] = useState<Blob | null>(null);
  const [manualActionLoading, setManualActionLoading] = useState(false);
  const [manualMessage, setManualMessage] = useState("");
  const [manualError, setManualError] = useState("");
  const [manualLastSavedCountNumber, setManualLastSavedCountNumber] = useState("");
  const [manualCertificationCheckLoading, setManualCertificationCheckLoading] = useState(false);
  const [manualCertificationStatus, setManualCertificationStatus] = useState<
    "idle" | "certified" | "not-certified"
  >("idle");
  const [manualCertificationMessage, setManualCertificationMessage] = useState("");
  const [manualLastCheckedPair, setManualLastCheckedPair] = useState<{
    product: string;
    email: string;
  } | null>(null);
  const [isManualConfirmationModalOpen, setManualConfirmationModalOpen] = useState(false);

  const manualSelectedProductDefinition = useMemo(
    () =>
      manualSelectedProduct && isProductKey(manualSelectedProduct)
        ? getProductDefinition(manualSelectedProduct)
        : undefined,
    [manualSelectedProduct]
  );

  const manualCertificateConfig = manualSelectedProductDefinition?.certificate;
  const requiresManualFieldCode =
    manualSelectedProduct === "hygienehb" || manualSelectedProduct === "hygienehben";

  const certificateEnabledProducts = useMemo(
    () =>
      availableProducts.filter(
        (product) => isProductKey(product.key) && Boolean(getProductDefinition(product.key)?.certificate)
      ),
    [availableProducts]
  );

  const manualSelectedProductTitle = useMemo(
    () =>
      availableProducts.find((product) => product.key === manualSelectedProduct)?.title ||
      manualSelectedProduct,
    [availableProducts, manualSelectedProduct]
  );

  const normalizedManualEmail = manualEmail.trim().toLowerCase();

  const isManualCertificationCheckCurrent =
    Boolean(manualSelectedProduct) &&
    Boolean(normalizedManualEmail) &&
    manualLastCheckedPair?.product === manualSelectedProduct &&
    manualLastCheckedPair?.email === normalizedManualEmail &&
    manualCertificationStatus !== "idle";

  const generateManualCertificatePdf = useCallback(
    async (countValue: string) => {
      if (!manualCertificateConfig) {
        return null;
      }

      return generateCertificatePdf({
        templatePath: manualCertificateConfig.templatePath,
        name: manualName,
        surname: manualSurname,
        birthDate: manualBirthDate,
        productCode: manualProductCode,
        date: manualDate,
        countNumber: countValue,
        hours: manualHours,
        fieldCode: manualFieldCode,
      });
    },
    [
      manualBirthDate,
      manualCertificateConfig,
      manualDate,
      manualFieldCode,
      manualHours,
      manualName,
      manualProductCode,
      manualSurname,
    ]
  );

  useEffect(() => {
    const loadNextManualCount = async () => {
      try {
        const nextCountNumber = await fetchNextCountNumber();
        if (nextCountNumber) {
          setManualCountNumber(nextCountNumber);
        }
      } catch (error) {
        console.error("Error fetching next manual count number:", error);
      }
    };

    loadNextManualCount();
  }, []);

  useEffect(() => {
    if (!manualCertificateConfig) {
      setManualFieldCode("");
      setManualProductCode("");
      setManualHours("");
      return;
    }

    if (requiresManualFieldCode) {
      setManualFieldCode("");
    } else {
      setManualFieldCode(manualCertificateConfig.fieldCode);
    }

    setManualProductCode(manualCertificateConfig.productCode);
    setManualHours(manualCertificateConfig.hours);
  }, [manualCertificateConfig, requiresManualFieldCode]);

  useEffect(() => {
    setManualCertificationStatus("idle");
    setManualCertificationMessage("");
    setManualLastCheckedPair(null);
    setManualConfirmationModalOpen(false);
  }, [manualSelectedProduct, normalizedManualEmail]);

  useEffect(() => {
    const refreshManualPdf = async () => {
      if (
        !manualCertificateConfig ||
        !manualName.trim() ||
        !manualSurname.trim() ||
        !manualBirthDate ||
        !manualProductCode ||
        !manualHours ||
        !manualFieldCode ||
        !manualCountNumber
      ) {
        setManualPdfBlob(null);
        return;
      }

      const blob = await generateManualCertificatePdf(manualCountNumber);
      setManualPdfBlob(blob);
    };

    refreshManualPdf();
  }, [
    generateManualCertificatePdf,
    manualBirthDate,
    manualCertificateConfig,
    manualCountNumber,
    manualFieldCode,
    manualHours,
    manualName,
    manualProductCode,
    manualSurname,
  ]);

  const handleManualProductChange = useCallback((nextProduct: string) => {
    setManualSelectedProduct(nextProduct);
    setManualMessage("");
    setManualError("");
    setManualLastSavedCountNumber("");
  }, []);

  const handleManualCertificationCheck = useCallback(async () => {
    if (!manualSelectedProduct || !manualEmail.trim()) {
      setManualError("Pasirinkite produktą ir įveskite el. pašto adresą prieš tikrinant sertifikavimą.");
      return;
    }

    setManualCertificationCheckLoading(true);
    setManualError("");
    setManualCertificationMessage("");
    setManualConfirmationModalOpen(false);

    try {
      const isCertified = await checkCertificateUserExists(manualSelectedProduct, manualEmail.trim());

      setManualCertificationStatus(isCertified ? "certified" : "not-certified");
      setManualLastCheckedPair({
        product: manualSelectedProduct,
        email: manualEmail.trim().toLowerCase(),
      });
      setManualCertificationMessage(
        isCertified
          ? "Naudotojas šiame produkte jau turi sertifikato įrašą. Registruojant bus reikalingas papildomas patvirtinimas."
          : "Naudotojas šiame produkte dar nėra sertifikuotas. Registraciją galima tęsti be papildomo patvirtinimo."
      );
    } catch (error) {
      setManualCertificationStatus("idle");
      setManualLastCheckedPair(null);
      setManualError(
        error instanceof Error ? error.message : "Nepavyko patikrinti sertifikavimo būsenos"
      );
    } finally {
      setManualCertificationCheckLoading(false);
    }
  }, [manualEmail, manualSelectedProduct]);

  const performManualCompletion = useCallback(async () => {
    if (
      !manualSelectedProduct ||
      !manualName.trim() ||
      !manualSurname.trim() ||
      !manualBirthDate ||
      !manualEmail.trim() ||
      !manualFieldCode ||
      !manualProductCode ||
      !manualHours
    ) {
      setManualError("Užpildykite visus privalomus laukus.");
      return;
    }

    setManualActionLoading(true);
    setManualMessage("");
    setManualError("");

    try {
      const finalPdfBlob = await generateManualCertificatePdf(manualCountNumber);

      if (!finalPdfBlob) {
        setManualError("Nepavyko sugeneruoti pažymėjimo PDF.");
        setManualActionLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("selectedProduct", manualSelectedProduct);
      formData.append("name", manualName.trim());
      formData.append("surname", manualSurname.trim());
      formData.append("birthDate", manualBirthDate);
      formData.append("email", manualEmail.trim());
      formData.append("fieldCode", manualFieldCode);
      formData.append("countNumber", manualCountNumber);
      formData.append("productCode", manualProductCode);
      formData.append("hours", manualHours);
      formData.append("date", manualDate);
      formData.append("company", manualCompany.trim());

      if (manualOptionalEmail.trim()) {
        formData.append("optionalEmail", manualOptionalEmail.trim());
      }

      formData.append("certificate", finalPdfBlob, "certificate.pdf");

      const response = await createAdminManualCertificate(formData);

      setManualLastSavedCountNumber(response.countNumber);
      setManualPdfBlob(finalPdfBlob);
      downloadPdfBlob(finalPdfBlob);
      setManualMessage(
        response.emailSent
          ? `Pažymėjimas užregistruotas, el. laiškas išsiųstas, numeris: ${response.countNumber}.`
          : `Pažymėjimas užregistruotas, tačiau el. laiško išsiųsti nepavyko. Numeris: ${response.countNumber}.`
      );
      setManualCertificationStatus("idle");
      setManualCertificationMessage("");
      setManualLastCheckedPair(null);
      setManualConfirmationModalOpen(false);

      const nextCountNumber = await fetchNextCountNumber();
      if (nextCountNumber) {
        setManualCountNumber(nextCountNumber);
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes("pasikeitė")) {
        try {
          const refreshedCountNumber = await fetchNextCountNumber();
          if (refreshedCountNumber) {
            setManualCountNumber(refreshedCountNumber);
          }
        } catch (refreshError) {
          console.error("Failed to refresh count number after mismatch:", refreshError);
        }
      }

      setManualError(error instanceof Error ? error.message : "Nepavyko užregistruoti pažymėjimo");
    } finally {
      setManualActionLoading(false);
    }
  }, [
    generateManualCertificatePdf,
    manualBirthDate,
    manualCompany,
    manualCountNumber,
    manualDate,
    manualEmail,
    manualFieldCode,
    manualHours,
    manualName,
    manualOptionalEmail,
    manualProductCode,
    manualSelectedProduct,
    manualSurname,
  ]);

  const handleManualCompletion = useCallback(() => {
    if (!isManualCertificationCheckCurrent) {
      setManualError("Pirmiausia patikrinkite, ar naudotojas jau sertifikuotas pasirinktame produkte.");
      return;
    }

    if (manualCertificationStatus === "certified") {
      setManualConfirmationModalOpen(true);
      return;
    }

    void performManualCompletion();
  }, [isManualCertificationCheckCurrent, manualCertificationStatus, performManualCompletion]);

  const confirmManualCompletion = useCallback(() => {
    setManualConfirmationModalOpen(false);
    void performManualCompletion();
  }, [performManualCompletion]);

  return {
    HB_FIELD_CODE_OPTIONS,
    certificateEnabledProducts,
    requiresManualFieldCode,
    manualSelectedProduct,
    manualName,
    manualSurname,
    manualBirthDate,
    manualEmail,
    manualOptionalEmail,
    manualCompany,
    manualFieldCode,
    manualProductCode,
    manualHours,
    manualDate,
    manualCountNumber,
    manualPdfBlob,
    manualActionLoading,
    manualMessage,
    manualError,
    manualLastSavedCountNumber,
    manualCertificationCheckLoading,
    manualCertificationStatus,
    manualCertificationMessage,
    manualSelectedProductTitle,
    isManualCertificationCheckCurrent,
    isManualConfirmationModalOpen,
    setManualName: (value: string) => setManualName(sanitizeName(value)),
    setManualSurname: (value: string) => setManualSurname(sanitizeName(value)),
    setManualBirthDate,
    setManualEmail,
    setManualOptionalEmail,
    setManualCompany,
    setManualFieldCode,
    setManualDate,
    handleManualProductChange,
    handleManualCertificationCheck,
    handleManualCompletion,
    closeManualConfirmationModal: () => setManualConfirmationModalOpen(false),
    confirmManualCompletion,
    downloadManualPreview: () => downloadPdfBlob(manualPdfBlob),
  };
};
